import { FingerprintUtil } from './FingerprintUtil';

export class AuthUtil {
    static readonly LS_KEYS = {
      DEVICE_ID: 'device_id',
      JWT_ACCESS_TOKEN: 'jwt_access_token',
      JWT_ACCESS_TOKEN_VALID_UNTIL: 'jwt_access_token_valid_until',
      JWT_REFRESH_TOKEN: 'jwt_refresh_token',
      JWT_REFRESH_TOKEN_VALID_UNTIL: 'jwt_refresh_token_valid_until',
      BROWSER_FINGERPRINT: 'browser_fingerprint_hash',
    };

    private static getItem(key: string): string | null {
      return localStorage.getItem(key);
    }

    private static setItem(key: string, value: string): void {
      localStorage.setItem(key, value);
    }

    private static removeItem(key: string): void {
      localStorage.removeItem(key);
    }

    /**
     * Get the device id from local storage
     */
    static getDeviceId(): string | null {
      return this.getItem(this.LS_KEYS.DEVICE_ID);
    }

    /**
     * Set the device id in local storage
     */
    static setDeviceId(deviceId: string): void {
      this.setItem(this.LS_KEYS.DEVICE_ID, deviceId);
    }

    /**
     * Get the payload from a JWT token
     */
    static getTokenPayload(accessToken: string): any {
      try {
        if (!accessToken || typeof accessToken !== 'string') {
          return null;
        }

        const parts = accessToken.split('.');
        if (parts.length < 2 || !parts[1]) {
          return null;
        }

        return JSON.parse(atob(parts[1]));
      } catch (e) {
        console.error('Invalid token', e);
        return null;
      }
    }

    /**
     * Clear all auth values from local storage (including fingerprint cache)
     */
    static clearTokens(): void {
      Object.values(this.LS_KEYS).forEach(key => this.removeItem(key));
      FingerprintUtil.clearCache();
    }

    /**
     * Get the refresh token from local storage
     */
    static getRefreshToken(): string | null {
      return this.getItem(this.LS_KEYS.JWT_REFRESH_TOKEN);
    }

    /**
     * Set the opaque refresh token in local storage
     * Opaque tokens cannot be decoded - expiry must be set separately
     */
    static setRefreshToken(token: string): string {
      this.setItem(this.LS_KEYS.JWT_REFRESH_TOKEN, token);
      return token;
    }

    /**
     * Set the refresh token with explicit expiry from the server
     * Used for opaque tokens that cannot be decoded
     */
    static setRefreshTokenWithExpiry(token: string, expiresAtUnixSeconds: number): string {
      this.setItem(this.LS_KEYS.JWT_REFRESH_TOKEN, token);
      this.setItem(this.LS_KEYS.JWT_REFRESH_TOKEN_VALID_UNTIL, expiresAtUnixSeconds.toString());
      return token;
    }

    /**
     * Get refresh token valid until timestamp in seconds
     */
    static getRefreshTokenValidUntil(): number {
      return +(this.getItem(this.LS_KEYS.JWT_REFRESH_TOKEN_VALID_UNTIL) ?? 0);
    }

    /**
     * Check if the refresh token is expired
     */
    static getRefreshTokenExpired(): boolean {
      return this.getRefreshTokenValidUntil() * 1000 < Date.now();
    }

    /**
     * Get access token valid until timestamp in seconds
     */
    static getAccessTokenValidUntil(): number {
      return +(this.getItem(this.LS_KEYS.JWT_ACCESS_TOKEN_VALID_UNTIL) ?? 0);
    }

    /**
     * Check if the access token is expired
     */
    static getAccessTokenExpired(): boolean {
      return this.getAccessTokenValidUntil() * 1000 < Date.now();
    }

    /**
     * Get the access token from local storage
     */
    static getAccessToken(): string | null {
      return this.getItem(this.LS_KEYS.JWT_ACCESS_TOKEN);
    }

    /**
     * Set the access token (JWT) in local storage
     */
    static setAccessToken(token: string): string {
      this.setItem(this.LS_KEYS.JWT_ACCESS_TOKEN, token);
      const decodedData = this.getTokenPayload(token);
      if (decodedData && decodedData.exp) {
        this.setItem(this.LS_KEYS.JWT_ACCESS_TOKEN_VALID_UNTIL, decodedData.exp.toString());
      }
      return token;
    }
  }
